<template>
  <div class="tw-my-12">
    <h2 class="tw-text-6xl tw-font-bold tw-text-center tw-mb-16">Tell us about your guests!</h2>

    <div class="lg:tw-w-3/4 xl:tw-w-3/5 tw-mx-auto">
      <div class="tw-mb-16 tw-mx-8" v-if="state.cateringOrder && state.cateringOrder.guest_count != 0">
        <div class="tw-text-right tw-text-lg tw-mr-3 tw-mb-4">
          <strong class="tw-font-black">{{ guestsAccountedFor }}/{{ state.cateringOrder.guest_count}}</strong> guests accounted for
        </div>
        <ProgressBar :percentage="percentageAccountedFor" />
      </div>

      <div :class="['tw-flex tw-justify-center tw-flex-wrap lg:tw-flex-nowrap', {'tw-mb-12 lg:tw-mb-24': isValid}, {'tw-mb-6 lg:tw-mb-12': !isValid}]" v-if="state.cateringOrder">
        <GuestHungerItem
          hungerLevel="Light Eaters"
          hungerLevelDetails="lorem ipsum"
          :requiresAmount="props.cateringOrder.guest_count != 0"
          iconType="light"
          :amount="state.cateringOrder.light_eaters" 
          :max="state.cateringOrder.guest_count"
          @amountChanged="lightAmountChanged" />

        <GuestHungerItem
          hungerLevel="Typical Eaters"
          hungerLevelDetails="lorem ipsum"
          :requiresAmount="props.cateringOrder.guest_count != 0"
          iconType="typical"
          :amount="state.cateringOrder.regular_eaters"
          :max="state.cateringOrder.guest_count"
          @amountChanged="typicalAmountChanged" />
          
        <GuestHungerItem
          hungerLevel="Hungry Eaters"
          hungerLevelDetails="lorem ipsum"
          :requiresAmount="props.cateringOrder.guest_count != 0"
          iconType="hungry"
          :amount="state.cateringOrder.hungry_eaters"
          :max="state.cateringOrder.guest_count"
          @amountChanged="hungryAmountChanged" />
      </div>
    </div>

    <div class="tw-mb-6 lg:tw-mb-12 tw-mx-12 tw-flex tw-items-center tw-justify-center tw-text-red-500" v-if="!isValid">
      <div>
        The provided guest counts do not match the amount of anticipated guests entered in the previous step: <strong class="tw-font-black" v-if="state.cateringOrder && state.cateringOrder!.guest_count"> {{state.cateringOrder!.guest_count}} guests</strong>
      </div>
    </div>

    <div class="tw-border-t tw-pt-16 tw-border-gray-300" v-if="state.cateringOrder && false">
      <div class="md:tw-w-3/4 lg:tw-w-3/5 tw-mx-auto md:tw-flex tw-justify-center">
        <div class="tw-mx-12">
          <h3 class="tw-text-3xl tw-font-bold tw-mb-4">Allergies</h3>
          
          <TwCheckbox
            id="restriction-glutenAllergen"
            key="restriction-glutenAllergen"
            label="Gluten"
            value="1"
            class="tw-mb-4"
            :checked="state.cateringOrder!.dietary_restrictions.glutenAllergen"
            @on-change="state.cateringOrder!.dietary_restrictions.glutenAllergen = !state.cateringOrder!.dietary_restrictions.glutenAllergen"
          />

          <TwCheckbox
            id="restriction-dairyAllergen"
            key="restriction-dairyAllergen"
            label="Dairy"
            value="1"
            class="tw-mb-4"
            :checked="state.cateringOrder!.dietary_restrictions.dairyAllergen"
            @on-change="state.cateringOrder!.dietary_restrictions.dairyAllergen = !state.cateringOrder!.dietary_restrictions.dairyAllergen"
          />

          <TwCheckbox
            id="restriction-nutAllergen"
            key="restriction-nutAllergen"
            label="Nuts"
            value="1"
            class="tw-mb-4"
            :checked="state.cateringOrder!.dietary_restrictions.nutAllergen"
            @on-change="state.cateringOrder!.dietary_restrictions.nutAllergen = !state.cateringOrder!.dietary_restrictions.nutAllergen"
          />
        </div>

        <div class="tw-mx-12">
          <h3 class="tw-text-3xl tw-font-bold tw-mb-4">Dietary Restrictions</h3>

          <TwCheckbox
            id="restriction-vegan"
            key="restriction-vegan"
            label="Vegan"
            value="1"
            class="tw-mb-4"
            :checked="state.cateringOrder!.dietary_restrictions.vegan"
            @on-change="state.cateringOrder!.dietary_restrictions.vegan = !state.cateringOrder!.dietary_restrictions.vegan"
          />

          <TwCheckbox
            id="restriction-vegetarian"
            key="restriction-vegetarian"
            label="Vegetarian"
            value="1"
            class="tw-mb-4"
            :checked="state.cateringOrder!.dietary_restrictions.vegetarian"
            @on-change="state.cateringOrder!.dietary_restrictions.vegetarian = !state.cateringOrder!.dietary_restrictions.vegetarian"
          />

          <TwCheckbox
            id="restriction-heartHealthy"
            key="restriction-heartHealthy"
            label="Heart Healthy"
            value="1"
            class="tw-mb-4"
            :checked="state.cateringOrder!.dietary_restrictions.heartHealthy"
            @on-change="state.cateringOrder!.dietary_restrictions.heartHealthy = !state.cateringOrder!.dietary_restrictions.heartHealthy"
          />

        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    reactive, computed, onMounted
  } from 'vue';
  import type {PropType} from 'vue';
  import { watchEffect } from 'vue';

  // Data
  import type CateringOrder from '../../types/CateringOrder'

  import ProgressBar from '../../components/ProgressBar.vue'
  import GuestHungerItem from './_GuestHungerItem.vue'
  import TwCheckbox from '../../components/TwCheckbox.vue'

  const emit = defineEmits(['setCateringOrder']);

  const props = defineProps({
    cateringOrder: {
      type: (Object as PropType<CateringOrder>),
      default: null,
    }
  });

  const state = reactive({
    cateringOrder: null as (CateringOrder | null)
   })

  onMounted(() => {
    state.cateringOrder = props.cateringOrder;
    if (state.cateringOrder && state.cateringOrder.guest_count > 0) {
      if ((state.cateringOrder.light_eaters + state.cateringOrder.regular_eaters + state.cateringOrder.hungry_eaters) == 0) {
        state.cateringOrder.regular_eaters = state.cateringOrder.guest_count;
      }
    }
  })

  watchEffect(() => {
    const cateringOrder = state.cateringOrder;
    if (cateringOrder === null) return;
    emit('setCateringOrder', cateringOrder);
  })

  const guestsAccountedFor = computed(() => {
    if (state.cateringOrder) {
      return (state.cateringOrder.light_eaters + state.cateringOrder.regular_eaters + state.cateringOrder.hungry_eaters);
    } else {
      return 0
    }
  })

  const percentageAccountedFor = computed(() => {
    if (state.cateringOrder && state.cateringOrder!.guest_count && guestsAccountedFor.value > 0 && state.cateringOrder.guest_count > 0) {
      return Math.min(100, ((guestsAccountedFor.value/state.cateringOrder!.guest_count) * 100))
    } else {
      return 0;
    }
  })

  const isValid = computed(() => {
    if (state.cateringOrder && state.cateringOrder!.guest_count && guestsAccountedFor.value == state.cateringOrder.guest_count) {
      return true
    } else {
      return false
    }
  })

  const lightAmountChanged = (amount: number) => {
    state.cateringOrder!.light_eaters = amount;
  }

  const typicalAmountChanged = (amount: number) => {
    state.cateringOrder!.regular_eaters = amount;
  }

  const hungryAmountChanged = (amount: number) => {
    state.cateringOrder!.hungry_eaters = amount;
  }

</script>
